// Recent files (AC-UX-8, D-38): kept in this browser only (IndexedDB), never
// sent anywhere. Each entry holds the file as it was opened and, for files
// that are not encrypted, a small picture of page 1. The list can be turned
// off, which also clears it.

export interface RecentFile {
  id: string;
  name: string;
  size: number;
  openedAt: number;
  encrypted: boolean;
  /** a picture of page 1 (PNG); none for encrypted files */
  thumb?: Blob;
}

interface Stored extends RecentFile {
  bytes: Blob;
}

const DB = 'flexpdf';
const STORE = 'recent';
/** at most this many files… */
export const RECENT_MAX = 12;
/** …and this many bytes; larger files are not kept */
const RECENT_BYTES = 300 * 1024 * 1024;
const FILE_MAX = 100 * 1024 * 1024;
const OFF_KEY = 'flexpdf-recent';

export function recentEnabled(): boolean {
  try {
    return localStorage.getItem(OFF_KEY) !== 'off';
  } catch {
    return false;
  }
}

export async function setRecentEnabled(on: boolean): Promise<void> {
  try {
    if (on) localStorage.removeItem(OFF_KEY);
    else localStorage.setItem(OFF_KEY, 'off');
  } catch {
    // Storage blocked: nothing is kept anyway.
  }
  if (!on) await clearRecent();
}

function db(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function run<T>(mode: IDBTransactionMode, work: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  const d = await db();
  try {
    return await new Promise<T | undefined>((resolve, reject) => {
      const tx = d.transaction(STORE, mode);
      const req = work(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(req ? req.result : undefined);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    d.close();
  }
}

/** The same file opened again is one entry: name, size and a hash of its start and end. */
function idOf(name: string, bytes: Uint8Array): string {
  let h = 0x811c9dc5;
  const mix = (from: number, to: number) => {
    for (let i = from; i < to; i++) h = Math.imul(h ^ bytes[i], 0x01000193);
  };
  const part = 1 << 20;
  mix(0, Math.min(part, bytes.length));
  // A slice from the middle too: two files of one size that differ only in the middle are not the same entry.
  const mid = Math.floor(bytes.length / 2);
  mix(Math.max(part, mid - part / 2), Math.min(bytes.length, mid + part / 2));
  mix(Math.max(part, bytes.length - part), bytes.length);
  return `${name}:${bytes.length}:${(h >>> 0).toString(16)}`;
}

/** Newest first, without the file bytes. */
export async function listRecent(): Promise<RecentFile[]> {
  if (!recentEnabled()) return [];
  try {
    const all = ((await run<Stored[]>('readonly', (s) => s.getAll())) ?? []) as Stored[];
    return all.sort((a, b) => b.openedAt - a.openedAt).map(({ bytes: _bytes, ...rest }) => rest);
  } catch {
    return [];
  }
}

export async function recentBytes(id: string): Promise<Uint8Array | undefined> {
  const e = (await run<Stored>('readonly', (s) => s.get(id))) as Stored | undefined;
  return e ? new Uint8Array(await e.bytes.arrayBuffer()) : undefined;
}

/** Adds (or moves to the top) a file just opened; the oldest go when the list is full. */
export async function addRecent(name: string, bytes: Uint8Array, encrypted: boolean, thumb?: Blob): Promise<void> {
  if (!recentEnabled() || bytes.length > FILE_MAX) return;
  const entry: Stored = { id: idOf(name, bytes), name, size: bytes.length, openedAt: Date.now(), encrypted, thumb: encrypted ? undefined : thumb, bytes: new Blob([bytes.slice()], { type: 'application/pdf' }) };
  await run('readwrite', (s) => {
    s.put(entry);
  });
  const all = ((await run<Stored[]>('readonly', (s) => s.getAll())) ?? []) as Stored[];
  all.sort((a, b) => b.openedAt - a.openedAt);
  let total = 0;
  const drop = all.filter((e, k) => {
    total += e.size;
    return k >= RECENT_MAX || (k > 0 && total > RECENT_BYTES);
  });
  if (drop.length) await run('readwrite', (s) => drop.forEach((e) => s.delete(e.id)));
}

export async function removeRecent(id: string): Promise<void> {
  await run('readwrite', (s) => {
    s.delete(id);
  });
}

/** Empties the store. Throws if that fails: "off" must not look done while the files are still stored. */
export async function clearRecent(): Promise<void> {
  await run('readwrite', (s) => {
    s.clear();
  });
}
